/**
 * 录像回放页：把 JSONL 事件流按时间轴重放。
 * 替代老系统的 logplay.jsp + WCSClient 的 StartPlayBack。
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { formatSize, formatTime, type RecordRow } from '../lib/adminApi'
import { assetURL } from '../lib/endpoint'
import { Playback, type PlaybackState } from '../lib/playback'
import { WhiteboardView } from '../components/WhiteboardView'

interface Props {
  record: RecordRow
  onClose: () => void
}

const SPEEDS = [0.5, 1, 1.5, 2, 4] as const

export function PlaybackPage({ record, onClose }: Props) {
  const [pb] = useState(() => new Playback())
  const [loaded, setLoaded] = useState(false)
  const [err, setErr] = useState('')
  const [posMs, setPosMs] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState<number>(1)
  const [state, setState] = useState<PlaybackState | null>(null)

  const rafRef = useRef<number | null>(null)
  const lastTickRef = useRef(0)

  // 载入事件流。
  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const resp = await fetch(assetURL(record.url))
        if (!resp.ok) throw new Error(`加载录像失败（HTTP ${resp.status}）`)
        const text = await resp.text()
        if (!alive) return
        pb.loadJSONL(text)
        setState(pb.stateAt(0))
        setLoaded(true)
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : '加载录像失败')
      }
    })()
    return () => {
      alive = false
    }
  }, [record.url, pb])

  // 播放循环。
  useEffect(() => {
    if (!playing || !loaded) return

    lastTickRef.current = performance.now()
    const step = (now: number) => {
      const dt = (now - lastTickRef.current) * speed
      lastTickRef.current = now

      setPosMs((prev) => {
        const next = prev + dt
        if (next >= pb.duration) {
          setPlaying(false)
          return pb.duration
        }
        return next
      })
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [playing, speed, loaded, pb])

  // 位置变化时重算状态。
  //
  // 每帧全量重算在几千条事件下也只有毫秒级，换来的是「任意拖动都正确」，
  // 比维护一份可增量前进又要能后退的状态机简单得多，也不会漂移。
  useEffect(() => {
    if (!loaded) return
    setState(pb.stateAt(posMs))
  }, [posMs, loaded, pb])

  const markers = useMemo(() => (loaded ? pb.markers() : []), [loaded, pb])

  if (err) {
    return (
      <div className="pb-page">
        <header className="pb-head">
          <button onClick={onClose}>← 返回</button>
          <strong>{record.title || record.recordId}</strong>
        </header>
        <div className="pb-error">{err}</div>
      </div>
    )
  }

  if (!loaded || !state) {
    return (
      <div className="pb-page">
        <header className="pb-head">
          <button onClick={onClose}>← 返回</button>
          <strong>{record.title || record.recordId}</strong>
        </header>
        <div className="pb-loading">
          <div className="spinner" />
          <p>正在载入录像…</p>
        </div>
      </div>
    )
  }

  const page = state.pages.find((p) => p.index === state.currentPage) ?? state.pages[0]!

  return (
    <div className="pb-page">
      <header className="pb-head">
        <button onClick={onClose}>← 返回</button>
        <strong>{record.title || record.recordId}</strong>
        <span className="dim">
          {formatTime(record.beginTime)} · {formatSize(record.size)} · {pb.eventCount} 个事件
        </span>
      </header>

      <div className="pb-body">
        <main className="pb-stage">
          <WhiteboardView page={page} />
          <div className="pb-page-label">
            第 {state.currentPage + 1} 页 / 共 {state.pages.length} 页
          </div>
        </main>

        <aside className="pb-side">
          <div className="panel">
            <div className="panel-head">
              当时在场 <span className="badge">{state.members.size}</span>
            </div>
            <ul className="member-list">
              {[...state.members.entries()].map(([id, name]) => (
                <li key={id} className={`member${state.mics.includes(id) ? ' on-mic' : ''}`}>
                  <span className="member-name">{name}</span>
                  {state.mics.includes(id) && <em className="tag tag-mic">发言中</em>}
                </li>
              ))}
              {state.members.size === 0 && <li className="chat-empty">—</li>}
            </ul>
          </div>

          <div className="panel chat-panel">
            <div className="panel-head">课堂交流</div>
            <div className="chat-list">
              {state.messages.slice(-100).map((m, i) => (
                <div key={i} className={`chat-msg${m.broadcast ? ' broadcast' : ''}`}>
                  <div className="chat-meta">
                    <span className="chat-name">{m.name}</span>
                    {m.broadcast && <em className="tag tag-notice">公告</em>}
                  </div>
                  <div className="chat-text">{m.text}</div>
                </div>
              ))}
              {state.messages.length === 0 && <div className="chat-empty">此刻还没有发言</div>}
            </div>
          </div>
        </aside>
      </div>

      <footer className="pb-controls">
        <button className="primary" onClick={() => setPlaying((p) => !p)}>
          {playing ? '暂停' : posMs >= pb.duration ? '重播' : '播放'}
        </button>
        <button onClick={() => setPosMs(Math.max(0, posMs - 10000))} title="后退 10 秒">
          « 10s
        </button>
        <button
          onClick={() => setPosMs(Math.min(pb.duration, posMs + 10000))}
          title="前进 10 秒"
        >
          10s »
        </button>

        <div className="pb-track">
          <input
            type="range"
            min={0}
            max={Math.max(1, pb.duration)}
            value={posMs}
            onChange={(e) => {
              setPosMs(Number(e.target.value))
              if (posMs >= pb.duration) setPlaying(false)
            }}
          />
          <div className="pb-markers">
            {markers.map((m, i) => (
              <span
                key={i}
                className="pb-marker"
                style={{ left: `${(m.t / Math.max(1, pb.duration)) * 100}%` }}
                title={`${fmtClock(m.t)} ${m.label}`}
              />
            ))}
          </div>
        </div>

        <span className="pb-time mono">
          {fmtClock(posMs)} / {fmtClock(pb.duration)}
        </span>

        <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
          {SPEEDS.map((s) => (
            <option key={s} value={s}>
              {s}× 速
            </option>
          ))}
        </select>
      </footer>
    </div>
  )
}

/** 毫秒格式化成 MM:SS。 */
function fmtClock(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}
